import _ from 'lodash'
import { COMMA } from '@vzmi/component_ec_nodejs_util/dist/lib/parserUtil'
import { PROPOSAL_EXECUTE_STATUS } from '@vzmi/shp_shared_compo/constants/proposal'
import { convertDateByMoment } from '../../../../lib/renderUtil'
import { SCHEDULER } from '../QueryPage'
import { DATE_FORMAT } from '../../../lib/MomentHelper'
import { BATCH_PROPOSAL_TYPE, BATCH_TYPE_LIMIT_MAP } from '../BatchUploadImage'

// 將 api 回傳結果分成成功與失敗
export const getBatchResult = batchProposals => {
  const successProposalMap = {}
  const failedProposalMap = {}
  for (const [batchFailed = {}, proposal] of batchProposals) {
    const { message, params, error } = batchFailed
    if (!_.isEmpty(batchFailed)) {
      // 失敗從 request params 寫入賣場編號
      const listingId = _.get(params, 'listing.id')
      _.set(failedProposalMap, listingId, { message, params, error })
    } else {
      // 成功從 response proposal 寫入賣場編號
      const listingId = _.get(proposal, 'listing.id')
      _.set(successProposalMap, listingId, proposal)
    }
  }
  return {
    successProposalMap,
    failedProposalMap
  }
}

export const getListingsWithProposal = (listings, batchResult, batches = []) => {
  const {
    successProposalMap,
    failedProposalMap
  } = batchResult
  const updatedListings = _.map(listings, listing => {
    const listingId = listing.id
    const proposal = _.get(successProposalMap, listingId, {})
    const listingProposalId = (
      // 提案新增、更新成功時從 proposal 取得提案編號
      _.get(proposal, 'id') ||
      // 提案更新失敗時從送出 request 取得提案編號
      _.get(failedProposalMap, `${listingId}.params.id`)
    )
    if (!_.isUndefined(listingProposalId)) {
      // proposal listing 的 models 只會回傳「修改過」的 models
      // 所以需要和現有的 listing models 合併才能得到完整商品資料
      const combindedModelsListingFromProposal = getCombindedModelsListingFromProposal(
        proposal,
        listing
      )
      return {
        proposal,
        // origin
        ...listing,
        // listing from create / update proposal
        ...combindedModelsListingFromProposal,
        batches
      }
    }
    return listing
  })
  return updatedListings
}

export const getCombindedModelsListingFromProposal = (proposal, listing) => {
  const listingFromProposal = _.get(proposal, 'listing', {})
  const models = _.get(listingFromProposal, 'models', [])
  // 單、雙層屬性賣場的第一層屬性以 sku 組成對應表
  const proposalListingModelMapBySku = _.keyBy(models, 'sku')
  // 單、雙層屬性賣場裡全部的第二層屬性展開以 modelItem sku 組成對應表
  const proposalListingModelItemMapBySku = _.flow(
    () => _.concat(..._.map(models, model => _.get(model, 'items', []))),
    modelItems => _.keyBy(modelItems, 'sku')
  )()
  const convertedListingModelsFromProposal = _.flow(
    () => _.get(listing, 'models', []),
    listingModels => {
      const convertedListingModels = _.map(listingModels, (model = {}) => {
        const newModel = _.get(proposalListingModelMapBySku, model.sku, {})
        const modelItems = _.get(model, 'items', [])
        return {
          // 賣場取得的 models
          ...model,
          // 提案修改後取得的 models
          ...newModel,
          // 如果是雙層屬性賣場需要把 items 逐項確認是否有修改以提案的 modelItem 蓋掉舊的欄位
          ...(!_.isEmpty(modelItems) && {
            items: _.map(modelItems, modelItem => ({
              ...modelItem,
              ..._.get(proposalListingModelItemMapBySku, modelItem.sku, {})
            }))
          })
        }
      })
      return convertedListingModels
    }
  )()
  const combindedModelsListingFromProposal = {
    ...listingFromProposal,
    models: convertedListingModelsFromProposal
  }
  return combindedModelsListingFromProposal
}

export const getCombinedBatchResult = (prev, current) => {
  const {
    successProposalMap: prevSuccess,
    failedProposalMap: prevFailed
  } = prev
  const {
    successProposalMap: currentSuccess,
    failedProposalMap: currentFailed
  } = current
  const successProposalMap = {
    // 提案返回編輯再次完成提案後更新提案失敗將原本標註為更新成功的賣場移除
    ..._.omit(prevSuccess, _.keys(currentFailed)),
    ...currentSuccess
  }
  const failedProposalMap = {
    // 完成、送出提案後更新提案成功將原本標註為更新失敗的賣場移除
    ..._.omit(prevFailed, _.keys(currentSuccess)),
    ...currentFailed
  }
  return { successProposalMap, failedProposalMap }
}

export const getNotfoundListingIds = (listings, queryStringListingIds) => {
  const expectListingIds = _.split(queryStringListingIds, COMMA)
  const listingMapById = _.keyBy(listings, 'id')
  const notfoundListingIds = _.filter(expectListingIds, id => !(id in listingMapById))
  return notfoundListingIds
}

export const getFailedListingIds = (listings, failedMap) => {
  // 以目前賣場的排序取得更新失敗的賣場編號
  const failedListingIds = _.flow(
    () => _.filter(listings, listing => {
      return (listing.id in failedMap)
    }),
    updateFailedListings => _.map(updateFailedListings, listing => `${listing.id}`)
  )()
  return failedListingIds
}

export const getSkuMap = (map, object, listing) => {
  const listingId = _.get(listing, 'id')
  const listingName = _.get(listing, 'name')
  const sku = _.get(object, 'sku')
  const skuName = _.get(object, 'name')
  if (_.some([sku, skuName], _.isUndefined)) {
    return map
  }

  const partListing = {
    name: listingName,
    id: listingId,
    sku,
    skuName
  }
  if (sku in map) {
    map[sku] = [...map[sku], partListing]
    return map
  }

  map[sku] = [partListing]
  return map
}

export const getOrderedIds = listings => {
  const orderedIds = _.flow(
    () => _.map(listings, 'id'),
    _.orderBy
  )()
  return orderedIds
}

export const getDuplicatedListingsGroupBySku = listings => {
  // 全部 sku 對應表包含賣場 sku、第一層屬性 sku、第二層屬性 sku
  const duplicatedListingsGroupBySku = _.flow(
    () => _.reduce(listings, (map, listing) => {
      const models = _.get(listing, 'models', [])
      map = getSkuMap(map, listing, listing)
      for (const model of models) {
        const modelItems = _.get(model, 'items', [])
        map = getSkuMap(map, model, listing)
        for (const modelItem of modelItems) {
          map = getSkuMap(map, modelItem, listing)
        }
      }
      return map
    }, {}),
    listingsGroupBySku => _.reduce(listingsGroupBySku, (map, listings, sku) => {
      // 只有賣場 1 個賣場使用這個商品代表非共用不需要紀錄
      if (_.size(listings) === 1) {
        return map
      }

      // 比對以 sku 分群含有共用商品的賣場
      // 如果有不同 sku 但共用相同的賣場一樣則合併這些 sku
      const currentListingIds = getOrderedIds(listings)
      const duplicatedFromSameListingsSkus = _.filter(_.keys(map), compareSku => {
        const compareListingIds = getOrderedIds(_.get(map, compareSku, []))
        return _.isEqual(currentListingIds, compareListingIds)
      })
      if (_.isEmpty(duplicatedFromSameListingsSkus)) {
        map[sku] = listings
        return map
      }

      // 移除發現有相同共用賣場但已寫入 map 的 sku
      map = _.omit(map, duplicatedFromSameListingsSkus)
      // 將 sku 結合後寫入共用商品的賣場
      map[_.join([sku, ...duplicatedFromSameListingsSkus], COMMA)] = listings
      return map
    }, {})
  )()
  return duplicatedListingsGroupBySku
}

export const getDuplicatedListingsGroupByListingId = listings => {
  const duplicatedListingsGroupByListingId = _.flow(
    () => getDuplicatedListingsGroupBySku(listings),
    listingsGroupBySkuStr => _.reduce(listingsGroupBySkuStr, (map, listings, skusStr) => {
      for (const listing of listings) {
        const listingId = _.get(listing, 'id')
        // 過濾掉自己剩下的就是跟自己重複共用商品的賣場
        const duplicatedListings = _.filter(listings, listing => listing.id !== listingId)
        map[listingId] = [
          ..._.get(map, listingId, []),
          { sku: skusStr, listings: duplicatedListings }
        ]
      }
      return map
    }, {})
  )()
  return duplicatedListingsGroupByListingId
}

export const getFieldName = (prefix, field) => {
  if (_.isUndefined(prefix)) {
    return field
  }

  return `${prefix}.${field}`
}

export const getExecuteBatchTypeStatus = (proposal) => {
  const {
    executeStatus,
    executeDetail
  } = proposal

  switch (executeStatus) {
    case PROPOSAL_EXECUTE_STATUS.CANCELED:
      return '已取消'
    case PROPOSAL_EXECUTE_STATUS.FINISHED:
      return '已執行'
    case PROPOSAL_EXECUTE_STATUS.FAILED:
      return `執行失敗(${executeDetail})`
    default:
      return BATCH_TYPE_LIMIT_MAP[BATCH_PROPOSAL_TYPE.EXECUTE]
  }
}

export const getRevertBatchTypeStatus = (proposal) => {
  const {
    executeDetail,
    revertStatus,
    reverter,
    modifiedTs
  } = proposal

  switch (revertStatus) {
    case PROPOSAL_EXECUTE_STATUS.CANCELED:
      return '已取消'
    case PROPOSAL_EXECUTE_STATUS.FINISHED:
      return `${reverter === SCHEDULER
        ? '已回檔'
        : '已立即回檔'}; 實際執行時間：${convertDateByMoment(modifiedTs, DATE_FORMAT.SCHEDULE_SECONDS)}`
    case PROPOSAL_EXECUTE_STATUS.FAILED:
      return `執行失敗(${executeDetail})`
    default:
      return BATCH_TYPE_LIMIT_MAP[BATCH_PROPOSAL_TYPE.REVERT]
  }
}
